import { VistaAsset } from '@design-stack-vista/vista-assets-sdk'
import { useCallback } from 'react'
import { ActionInfo, buildActionInfo, clearTimer, getTimerDuration, startTimer, TIMERS } from '../../util'
import { usePersonalizationManager } from '../PersonalizationManager'
import { useIdentity } from '@vp/ubik-context'

interface UseDrawerHandlers {
  handleImageLoadStart(asset: VistaAsset): void
  handleImageLoadComplete(asset: VistaAsset): void
  handleImageLoadError(asset: VistaAsset): void
}

export const useDrawerHandlers = (): UseDrawerHandlers => {
  const { isSignedIn } = useIdentity()

  const { tracking } = usePersonalizationManager()

  const getActionInfo = useCallback(
    (asset: VistaAsset): ActionInfo => {
      const loadingDuration = getTimerDuration(TIMERS.LOADING_TIMER, asset.data?.id)
      const actionInfo = buildActionInfo({
        actionDuration: loadingDuration,
        file: asset,
        // this was previously coming from recoil state that wasn't updated anywhere and defaulted to false
        hasAssets: false,
        isSignedIn,
        uploadId: asset.data?.id,
      })

      clearTimer(TIMERS.LOADING_TIMER, asset.data?.id)

      return actionInfo
    },
    [isSignedIn],
  )

  const handleImageLoadStart = useCallback(
    () =>
      (asset: VistaAsset): void => {
        startTimer(TIMERS.LOADING_TIMER, asset.data?.id)
      },
    [],
  )

  const handleImageLoadComplete = useCallback(
    (asset: VistaAsset): void => {
      tracking.recordImageLoadingSuccess(getActionInfo(asset))
    },
    [tracking, getActionInfo],
  )

  const handleImageLoadError = useCallback(
    (asset: VistaAsset): void => {
      tracking.recordImageLoadingError(getActionInfo(asset))
    },
    [tracking, getActionInfo],
  )

  return {
    handleImageLoadStart,
    handleImageLoadComplete,
    handleImageLoadError,
  }
}
